export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type Database = {
  // Allows to automatically instantiate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: "14.5"
  }
  comms: {
    Tables: {
      announcements: {
        Row: {
          attachments: Json | null
          body: string
          created_at: string
          created_by: string
          id: string
          target_metadata: Json | null
          title: string
        }
        Insert: {
          attachments?: Json | null
          body: string
          created_at?: string
          created_by: string
          id?: string
          target_metadata?: Json | null
          title: string
        }
        Update: {
          attachments?: Json | null
          body?: string
          created_at?: string
          created_by?: string
          id?: string
          target_metadata?: Json | null
          title?: string
        }
        Relationships: []
      }
      digest_items: {
        Row: {
          first_reported_at: string
          id: string
          item_key: string
          last_seen_at: string
          payload: Json
          section: string
          source: string
          title: string
          url: string | null
        }
        Insert: {
          first_reported_at?: string
          id?: string
          item_key: string
          last_seen_at?: string
          payload?: Json
          section: string
          source: string
          title: string
          url?: string | null
        }
        Update: {
          first_reported_at?: string
          id?: string
          item_key?: string
          last_seen_at?: string
          payload?: Json
          section?: string
          source?: string
          title?: string
          url?: string | null
        }
        Relationships: []
      }
      emails: {
        Row: {
          agent_id: string | null
          agent_thread_id: string | null
          attachment_metadata: Json | null
          body_html: string | null
          body_text: string | null
          chunks_created: number | null
          created_at: string
          error_message: string | null
          from_email: string
          id: string
          processing_completed_at: string | null
          processing_started_at: string | null
          raw_payload: Json | null
          status: string
          subject: string | null
          to_email: string
          updated_at: string
        }
        Insert: {
          agent_id?: string | null
          agent_thread_id?: string | null
          attachment_metadata?: Json | null
          body_html?: string | null
          body_text?: string | null
          chunks_created?: number | null
          created_at?: string
          error_message?: string | null
          from_email: string
          id?: string
          processing_completed_at?: string | null
          processing_started_at?: string | null
          raw_payload?: Json | null
          status?: string
          subject?: string | null
          to_email: string
          updated_at?: string
        }
        Update: {
          agent_id?: string | null
          agent_thread_id?: string | null
          attachment_metadata?: Json | null
          body_html?: string | null
          body_text?: string | null
          chunks_created?: number | null
          created_at?: string
          error_message?: string | null
          from_email?: string
          id?: string
          processing_completed_at?: string | null
          processing_started_at?: string | null
          raw_payload?: Json | null
          status?: string
          subject?: string | null
          to_email?: string
          updated_at?: string
        }
        Relationships: []
      }
      meetings: {
        Row: {
          action_items: string | null
          chunks_created: number | null
          client_reference_id: string | null
          duration_seconds: number | null
          error_message: string | null
          fireflies_meeting_id: string
          host_email: string | null
          id: string
          keywords: Json | null
          meeting_date: string | null
          organizer_email: string | null
          participants: Json | null
          processing_completed_at: string | null
          processing_started_at: string | null
          raw_webhook: Json | null
          received_at: string
          status: string
          summary_overview: string | null
          summary_short: string | null
          title: string | null
          topics_discussed: Json | null
          transcript_url: string | null
          updated_at: string
        }
        Insert: {
          action_items?: string | null
          chunks_created?: number | null
          client_reference_id?: string | null
          duration_seconds?: number | null
          error_message?: string | null
          fireflies_meeting_id: string
          host_email?: string | null
          id?: string
          keywords?: Json | null
          meeting_date?: string | null
          organizer_email?: string | null
          participants?: Json | null
          processing_completed_at?: string | null
          processing_started_at?: string | null
          raw_webhook?: Json | null
          received_at?: string
          status?: string
          summary_overview?: string | null
          summary_short?: string | null
          title?: string | null
          topics_discussed?: Json | null
          transcript_url?: string | null
          updated_at?: string
        }
        Update: {
          action_items?: string | null
          chunks_created?: number | null
          client_reference_id?: string | null
          duration_seconds?: number | null
          error_message?: string | null
          fireflies_meeting_id?: string
          host_email?: string | null
          id?: string
          keywords?: Json | null
          meeting_date?: string | null
          organizer_email?: string | null
          participants?: Json | null
          processing_completed_at?: string | null
          processing_started_at?: string | null
          raw_webhook?: Json | null
          received_at?: string
          status?: string
          summary_overview?: string | null
          summary_short?: string | null
          title?: string | null
          topics_discussed?: Json | null
          transcript_url?: string | null
          updated_at?: string
        }
        Relationships: []
      }
      recipients: {
        Row: {
          announcement_id: string
          created_at: string
          id: string
          read_at: string | null
          user_id: string
        }
        Insert: {
          announcement_id: string
          created_at?: string
          id?: string
          read_at?: string | null
          user_id: string
        }
        Update: {
          announcement_id?: string
          created_at?: string
          id?: string
          read_at?: string | null
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "announcement_recipients_announcement_id_fkey"
            columns: ["announcement_id"]
            isOneToOne: false
            referencedRelation: "announcements"
            referencedColumns: ["id"]
          },
        ]
      }
      sender_allowlist: {
        Row: {
          created_at: string
          created_by: string | null
          email_pattern: string
          id: string
          is_active: boolean
          notes: string | null
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          email_pattern: string
          id?: string
          is_active?: boolean
          notes?: string | null
        }
        Update: {
          created_at?: string
          created_by?: string | null
          email_pattern?: string
          id?: string
          is_active?: boolean
          notes?: string | null
        }
        Relationships: []
      }
      tender_emails: {
        Row: {
          body_html: string | null
          body_text: string | null
          created_at: string
          error_message: string | null
          evaluation: string | null
          evaluation_details: Json | null
          evaluation_reasoning: string | null
          from_email: string
          id: string
          processing_completed_at: string | null
          processing_started_at: string | null
          raw_payload: Json | null
          reply_error: string | null
          reply_sent: boolean | null
          reply_sent_at: string | null
          status: string
          subject: string | null
          to_email: string
          updated_at: string
        }
        Insert: {
          body_html?: string | null
          body_text?: string | null
          created_at?: string
          error_message?: string | null
          evaluation?: string | null
          evaluation_details?: Json | null
          evaluation_reasoning?: string | null
          from_email: string
          id?: string
          processing_completed_at?: string | null
          processing_started_at?: string | null
          raw_payload?: Json | null
          reply_error?: string | null
          reply_sent?: boolean | null
          reply_sent_at?: string | null
          status?: string
          subject?: string | null
          to_email: string
          updated_at?: string
        }
        Update: {
          body_html?: string | null
          body_text?: string | null
          created_at?: string
          error_message?: string | null
          evaluation?: string | null
          evaluation_details?: Json | null
          evaluation_reasoning?: string | null
          from_email?: string
          id?: string
          processing_completed_at?: string | null
          processing_started_at?: string | null
          raw_payload?: Json | null
          reply_error?: string | null
          reply_sent?: boolean | null
          reply_sent_at?: string | null
          status?: string
          subject?: string | null
          to_email?: string
          updated_at?: string
        }
        Relationships: []
      }
      tender_evaluations: {
        Row: {
          evaluated_at: string
          evaluation: Json
          id: string
          item_key: string
        }
        Insert: {
          evaluated_at?: string
          evaluation: Json
          id?: string
          item_key: string
        }
        Update: {
          evaluated_at?: string
          evaluation?: Json
          id?: string
          item_key?: string
        }
        Relationships: []
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  fleet: {
    Tables: {
      drones: {
        Row: {
          created_at: string | null
          engine_hours: number
          engine_hours_since_maintenance: Json | null
          flight_hours: number
          flight_hours_since_maintenance: Json | null
          lease_end_date: string | null
          legacy_hours: Json | null
          maintenance_history: Json | null
          metadata: Json | null
          model: string | null
          organization_id: string | null
          ownership_type:
            | Database["public"]["Enums"]["drone_ownership_type"]
            | null
          picture_url: string | null
          serial_number: string
          sold_date: string | null
          status: Database["public"]["Enums"]["drone_status"] | null
          updated_at: string | null
        }
        Insert: {
          created_at?: string | null
          engine_hours?: number
          engine_hours_since_maintenance?: Json | null
          flight_hours?: number
          flight_hours_since_maintenance?: Json | null
          lease_end_date?: string | null
          legacy_hours?: Json | null
          maintenance_history?: Json | null
          metadata?: Json | null
          model?: string | null
          organization_id?: string | null
          ownership_type?:
            | Database["public"]["Enums"]["drone_ownership_type"]
            | null
          picture_url?: string | null
          serial_number: string
          sold_date?: string | null
          status?: Database["public"]["Enums"]["drone_status"] | null
          updated_at?: string | null
        }
        Update: {
          created_at?: string | null
          engine_hours?: number
          engine_hours_since_maintenance?: Json | null
          flight_hours?: number
          flight_hours_since_maintenance?: Json | null
          lease_end_date?: string | null
          legacy_hours?: Json | null
          maintenance_history?: Json | null
          metadata?: Json | null
          model?: string | null
          organization_id?: string | null
          ownership_type?:
            | Database["public"]["Enums"]["drone_ownership_type"]
            | null
          picture_url?: string | null
          serial_number?: string
          sold_date?: string | null
          status?: Database["public"]["Enums"]["drone_status"] | null
          updated_at?: string | null
        }
        Relationships: []
      }
      file_objects: {
        Row: {
          blob_key: string | null
          content_type: string | null
          created_at: string
          created_by: string | null
          deleted_at: string | null
          deleted_by: string | null
          file_hash: string | null
          id: string
          name: string
          organization_id: string
          parent_id: string | null
          size_bytes: number | null
          updated_at: string
        }
        Insert: {
          blob_key?: string | null
          content_type?: string | null
          created_at?: string
          created_by?: string | null
          deleted_at?: string | null
          deleted_by?: string | null
          file_hash?: string | null
          id?: string
          name: string
          organization_id: string
          parent_id?: string | null
          size_bytes?: number | null
          updated_at?: string
        }
        Update: {
          blob_key?: string | null
          content_type?: string | null
          created_at?: string
          created_by?: string | null
          deleted_at?: string | null
          deleted_by?: string | null
          file_hash?: string | null
          id?: string
          name?: string
          organization_id?: string
          parent_id?: string | null
          size_bytes?: number | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "file_objects_parent_id_fkey"
            columns: ["parent_id"]
            isOneToOne: false
            referencedRelation: "file_objects"
            referencedColumns: ["id"]
          },
        ]
      }
      flight_analysis_results: {
        Row: {
          agent_interpretation: string | null
          agent_model: string | null
          analysis_completed_at: string | null
          analysis_started_at: string
          check_results: Json
          created_at: string
          file_type: string
          id: string
          object_id: string | null
          organization_id: string
          overall_verdict: string
          s3_key: string
          trigger_source: string
          triggered_by: string | null
        }
        Insert: {
          agent_interpretation?: string | null
          agent_model?: string | null
          analysis_completed_at?: string | null
          analysis_started_at?: string
          check_results: Json
          created_at?: string
          file_type: string
          id?: string
          object_id?: string | null
          organization_id: string
          overall_verdict: string
          s3_key: string
          trigger_source: string
          triggered_by?: string | null
        }
        Update: {
          agent_interpretation?: string | null
          agent_model?: string | null
          analysis_completed_at?: string | null
          analysis_started_at?: string
          check_results?: Json
          created_at?: string
          file_type?: string
          id?: string
          object_id?: string | null
          organization_id?: string
          overall_verdict?: string
          s3_key?: string
          trigger_source?: string
          triggered_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "flight_analysis_results_object_id_fkey"
            columns: ["object_id"]
            isOneToOne: false
            referencedRelation: "file_objects"
            referencedColumns: ["id"]
          },
        ]
      }
      flight_objects: {
        Row: {
          flight_id: string
          linked_at: string
          linked_by: string | null
          linked_by_organization_id: string | null
          object_id: string
          organization_id: string
          role: string
        }
        Insert: {
          flight_id: string
          linked_at?: string
          linked_by?: string | null
          linked_by_organization_id?: string | null
          object_id: string
          organization_id: string
          role?: string
        }
        Update: {
          flight_id?: string
          linked_at?: string
          linked_by?: string | null
          linked_by_organization_id?: string | null
          object_id?: string
          organization_id?: string
          role?: string
        }
        Relationships: [
          {
            foreignKeyName: "flight_objects_flight_id_fkey"
            columns: ["flight_id"]
            isOneToOne: false
            referencedRelation: "flights"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "flight_objects_flight_id_fkey"
            columns: ["flight_id"]
            isOneToOne: false
            referencedRelation: "flights_own_fleet"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "flight_objects_object_id_fkey"
            columns: ["object_id"]
            isOneToOne: false
            referencedRelation: "file_objects"
            referencedColumns: ["id"]
          },
        ]
      }
      flight_shares: {
        Row: {
          drone_serial: string | null
          flight_id: string
          granted_at: string
          granted_by: string | null
          grantee_organization_id: string
          granting_organization_id: string
          id: string
          permission: string
          revoked_at: string | null
          revoked_by: string | null
        }
        Insert: {
          drone_serial?: string | null
          flight_id: string
          granted_at?: string
          granted_by?: string | null
          grantee_organization_id: string
          granting_organization_id: string
          id?: string
          permission?: string
          revoked_at?: string | null
          revoked_by?: string | null
        }
        Update: {
          drone_serial?: string | null
          flight_id?: string
          granted_at?: string
          granted_by?: string | null
          grantee_organization_id?: string
          granting_organization_id?: string
          id?: string
          permission?: string
          revoked_at?: string | null
          revoked_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "flight_shares_flight_id_fkey"
            columns: ["flight_id"]
            isOneToOne: false
            referencedRelation: "flights"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "flight_shares_flight_id_fkey"
            columns: ["flight_id"]
            isOneToOne: false
            referencedRelation: "flights_own_fleet"
            referencedColumns: ["id"]
          },
        ]
      }
      flight_types: {
        Row: {
          created_at: string
          description: string | null
          display_order: number | null
          id: string
          name: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          display_order?: number | null
          id?: string
          name: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          description?: string | null
          display_order?: number | null
          id?: string
          name?: string
          updated_at?: string
        }
        Relationships: []
      }
      flights: {
        Row: {
          airspace_observer_id: string | null
          created_at: string | null
          customer_id: string | null
          drone_serial: string | null
          engine_hours: number | null
          file_hash: string | null
          flight_date: string | null
          flight_hours: number | null
          flight_type: string | null
          id: string
          log_basename: string | null
          metadata: Json | null
          object_id: string | null
          organization_id: string | null
          organization_name: string | null
          payload_id: number | null
          payload_operator_id: string | null
          pilot_id: string | null
          project_id: string | null
          s3_key: string | null
          size_bytes: number | null
          source: string
          uploaded_by: string | null
          weather: Json | null
        }
        Insert: {
          airspace_observer_id?: string | null
          created_at?: string | null
          customer_id?: string | null
          drone_serial?: string | null
          engine_hours?: number | null
          file_hash?: string | null
          flight_date?: string | null
          flight_hours?: number | null
          flight_type?: string | null
          id?: string
          log_basename?: string | null
          metadata?: Json | null
          object_id?: string | null
          organization_id?: string | null
          organization_name?: string | null
          payload_id?: number | null
          payload_operator_id?: string | null
          pilot_id?: string | null
          project_id?: string | null
          s3_key?: string | null
          size_bytes?: number | null
          source?: string
          uploaded_by?: string | null
          weather?: Json | null
        }
        Update: {
          airspace_observer_id?: string | null
          created_at?: string | null
          customer_id?: string | null
          drone_serial?: string | null
          engine_hours?: number | null
          file_hash?: string | null
          flight_date?: string | null
          flight_hours?: number | null
          flight_type?: string | null
          id?: string
          log_basename?: string | null
          metadata?: Json | null
          object_id?: string | null
          organization_id?: string | null
          organization_name?: string | null
          payload_id?: number | null
          payload_operator_id?: string | null
          pilot_id?: string | null
          project_id?: string | null
          s3_key?: string | null
          size_bytes?: number | null
          source?: string
          uploaded_by?: string | null
          weather?: Json | null
        }
        Relationships: [
          {
            foreignKeyName: "fk_flights_flight_type_name"
            columns: ["flight_type"]
            isOneToOne: false
            referencedRelation: "flight_types"
            referencedColumns: ["name"]
          },
          {
            foreignKeyName: "flights_drone_serial_fkey"
            columns: ["drone_serial"]
            isOneToOne: false
            referencedRelation: "drones"
            referencedColumns: ["serial_number"]
          },
          {
            foreignKeyName: "flights_object_id_fkey"
            columns: ["object_id"]
            isOneToOne: false
            referencedRelation: "file_objects"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "flights_payload_id_fkey"
            columns: ["payload_id"]
            isOneToOne: false
            referencedRelation: "payload"
            referencedColumns: ["id"]
          },
        ]
      }
      maintenance_config: {
        Row: {
          category: string | null
          counter_key: string
          created_at: string | null
          display_name: string
          enable_alerts: boolean
          hour_type: string
          interval_hours: number
          reset_by: string[]
          updated_at: string | null
        }
        Insert: {
          category?: string | null
          counter_key: string
          created_at?: string | null
          display_name: string
          enable_alerts?: boolean
          hour_type: string
          interval_hours: number
          reset_by: string[]
          updated_at?: string | null
        }
        Update: {
          category?: string | null
          counter_key?: string
          created_at?: string | null
          display_name?: string
          enable_alerts?: boolean
          hour_type?: string
          interval_hours?: number
          reset_by?: string[]
          updated_at?: string | null
        }
        Relationships: []
      }
      maintenance_documents: {
        Row: {
          chunk_index: number
          content: string
          created_at: string | null
          document_title: string
          document_type: string | null
          document_version: string | null
          drone_model: string | null
          embedding: string | null
          id: string
          manufacturer: string | null
          metadata: Json | null
          source_file_s3_key: string
          source_page_number: number | null
          updated_at: string | null
          uploaded_by_user_id: string | null
        }
        Insert: {
          chunk_index: number
          content: string
          created_at?: string | null
          document_title: string
          document_type?: string | null
          document_version?: string | null
          drone_model?: string | null
          embedding?: string | null
          id?: string
          manufacturer?: string | null
          metadata?: Json | null
          source_file_s3_key: string
          source_page_number?: number | null
          updated_at?: string | null
          uploaded_by_user_id?: string | null
        }
        Update: {
          chunk_index?: number
          content?: string
          created_at?: string | null
          document_title?: string
          document_type?: string | null
          document_version?: string | null
          drone_model?: string | null
          embedding?: string | null
          id?: string
          manufacturer?: string | null
          metadata?: Json | null
          source_file_s3_key?: string
          source_page_number?: number | null
          updated_at?: string | null
          uploaded_by_user_id?: string | null
        }
        Relationships: []
      }
      maintenance_records: {
        Row: {
          attachments: Json | null
          created_at: string
          drone_serial: string | null
          end_date: string
          id: string
          maintenance_type: string
          odoo_work_order_id: string | null
          organization_id: string | null
          parts_used: Json | null
          performed_by: string | null
          quality_checked: boolean | null
          quality_checked_by: string | null
          start_date: string
          status: string | null
          updated_at: string
          work_description: string | null
        }
        Insert: {
          attachments?: Json | null
          created_at?: string
          drone_serial?: string | null
          end_date: string
          id?: string
          maintenance_type: string
          odoo_work_order_id?: string | null
          organization_id?: string | null
          parts_used?: Json | null
          performed_by?: string | null
          quality_checked?: boolean | null
          quality_checked_by?: string | null
          start_date: string
          status?: string | null
          updated_at?: string
          work_description?: string | null
        }
        Update: {
          attachments?: Json | null
          created_at?: string
          drone_serial?: string | null
          end_date?: string
          id?: string
          maintenance_type?: string
          odoo_work_order_id?: string | null
          organization_id?: string | null
          parts_used?: Json | null
          performed_by?: string | null
          quality_checked?: boolean | null
          quality_checked_by?: string | null
          start_date?: string
          status?: string | null
          updated_at?: string
          work_description?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "maintenance_records_drone_serial_fkey"
            columns: ["drone_serial"]
            isOneToOne: false
            referencedRelation: "drones"
            referencedColumns: ["serial_number"]
          },
        ]
      }
      maintenance_types: {
        Row: {
          created_at: string
          created_by: string | null
          description: string | null
          display_order: number | null
          id: string
          is_active: boolean | null
          label: string
          updated_at: string
          value: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          display_order?: number | null
          id?: string
          is_active?: boolean | null
          label: string
          updated_at?: string
          value: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          display_order?: number | null
          id?: string
          is_active?: boolean | null
          label?: string
          updated_at?: string
          value?: string
        }
        Relationships: []
      }
      object_shares: {
        Row: {
          granted_at: string
          granted_by: string | null
          grantee_organization_id: string
          granting_organization_id: string
          id: string
          object_id: string
          object_name: string | null
          permission: string
          revoked_at: string | null
          revoked_by: string | null
        }
        Insert: {
          granted_at?: string
          granted_by?: string | null
          grantee_organization_id: string
          granting_organization_id: string
          id?: string
          object_id: string
          object_name?: string | null
          permission?: string
          revoked_at?: string | null
          revoked_by?: string | null
        }
        Update: {
          granted_at?: string
          granted_by?: string | null
          grantee_organization_id?: string
          granting_organization_id?: string
          id?: string
          object_id?: string
          object_name?: string | null
          permission?: string
          revoked_at?: string | null
          revoked_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "object_shares_object_id_fkey"
            columns: ["object_id"]
            isOneToOne: false
            referencedRelation: "file_objects"
            referencedColumns: ["id"]
          },
        ]
      }
      payload: {
        Row: {
          created_at: string | null
          id: number
          name: string
          updated_at: string | null
          weight: number | null
        }
        Insert: {
          created_at?: string | null
          id?: number
          name: string
          updated_at?: string | null
          weight?: number | null
        }
        Update: {
          created_at?: string | null
          id?: number
          name?: string
          updated_at?: string | null
          weight?: number | null
        }
        Relationships: []
      }
    }
    Views: {
      flight_analysis_results_own_fleet: {
        Row: {
          agent_interpretation: string | null
          agent_model: string | null
          analysis_completed_at: string | null
          analysis_started_at: string | null
          check_results: Json | null
          created_at: string | null
          file_type: string | null
          id: string | null
          object_id: string | null
          organization_id: string | null
          overall_verdict: string | null
          s3_key: string | null
          trigger_source: string | null
          triggered_by: string | null
        }
        Insert: {
          agent_interpretation?: string | null
          agent_model?: string | null
          analysis_completed_at?: string | null
          analysis_started_at?: string | null
          check_results?: Json | null
          created_at?: string | null
          file_type?: string | null
          id?: string | null
          object_id?: string | null
          organization_id?: string | null
          overall_verdict?: string | null
          s3_key?: string | null
          trigger_source?: string | null
          triggered_by?: string | null
        }
        Update: {
          agent_interpretation?: string | null
          agent_model?: string | null
          analysis_completed_at?: string | null
          analysis_started_at?: string | null
          check_results?: Json | null
          created_at?: string | null
          file_type?: string | null
          id?: string | null
          object_id?: string | null
          organization_id?: string | null
          overall_verdict?: string | null
          s3_key?: string | null
          trigger_source?: string | null
          triggered_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "flight_analysis_results_object_id_fkey"
            columns: ["object_id"]
            isOneToOne: false
            referencedRelation: "file_objects"
            referencedColumns: ["id"]
          },
        ]
      }
      flights_own_fleet: {
        Row: {
          airspace_observer_id: string | null
          created_at: string | null
          customer_id: string | null
          drone_serial: string | null
          engine_hours: number | null
          file_hash: string | null
          flight_date: string | null
          flight_hours: number | null
          flight_type: string | null
          id: string | null
          log_basename: string | null
          metadata: Json | null
          object_id: string | null
          organization_id: string | null
          organization_name: string | null
          payload_id: number | null
          payload_operator_id: string | null
          pilot_id: string | null
          project_id: string | null
          s3_key: string | null
          size_bytes: number | null
          source: string | null
          uploaded_by: string | null
          weather: Json | null
        }
        Insert: {
          airspace_observer_id?: string | null
          created_at?: string | null
          customer_id?: string | null
          drone_serial?: string | null
          engine_hours?: number | null
          file_hash?: string | null
          flight_date?: string | null
          flight_hours?: number | null
          flight_type?: string | null
          id?: string | null
          log_basename?: string | null
          metadata?: Json | null
          object_id?: string | null
          organization_id?: string | null
          organization_name?: string | null
          payload_id?: number | null
          payload_operator_id?: string | null
          pilot_id?: string | null
          project_id?: string | null
          s3_key?: string | null
          size_bytes?: number | null
          source?: string | null
          uploaded_by?: string | null
          weather?: Json | null
        }
        Update: {
          airspace_observer_id?: string | null
          created_at?: string | null
          customer_id?: string | null
          drone_serial?: string | null
          engine_hours?: number | null
          file_hash?: string | null
          flight_date?: string | null
          flight_hours?: number | null
          flight_type?: string | null
          id?: string | null
          log_basename?: string | null
          metadata?: Json | null
          object_id?: string | null
          organization_id?: string | null
          organization_name?: string | null
          payload_id?: number | null
          payload_operator_id?: string | null
          pilot_id?: string | null
          project_id?: string | null
          s3_key?: string | null
          size_bytes?: number | null
          source?: string | null
          uploaded_by?: string | null
          weather?: Json | null
        }
        Relationships: [
          {
            foreignKeyName: "fk_flights_flight_type_name"
            columns: ["flight_type"]
            isOneToOne: false
            referencedRelation: "flight_types"
            referencedColumns: ["name"]
          },
          {
            foreignKeyName: "flights_drone_serial_fkey"
            columns: ["drone_serial"]
            isOneToOne: false
            referencedRelation: "drones"
            referencedColumns: ["serial_number"]
          },
          {
            foreignKeyName: "flights_object_id_fkey"
            columns: ["object_id"]
            isOneToOne: false
            referencedRelation: "file_objects"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "flights_payload_id_fkey"
            columns: ["payload_id"]
            isOneToOne: false
            referencedRelation: "payload"
            referencedColumns: ["id"]
          },
        ]
      }
    }
    Functions: {
      current_org_has_flight_share: {
        Args: { p_flight_id: string; p_require_write?: boolean }
        Returns: boolean
      }
      current_org_has_flight_share_on_object: {
        Args: { p_object_id: string }
        Returns: boolean
      }
      current_org_has_object_share: {
        Args: { p_object_id: string; p_require_write?: boolean }
        Returns: boolean
      }
      soft_delete_object_subtree: {
        Args: { p_object_id: string }
        Returns: string[]
      }
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  inventory: {
    Tables: {
      items: {
        Row: {
          category: string | null
          created_at: string | null
          id: string
          location_id: string | null
          name: string
          notes: string | null
          organization_id: string
          quantity: number
          updated_at: string | null
        }
        Insert: {
          category?: string | null
          created_at?: string | null
          id?: string
          location_id?: string | null
          name: string
          notes?: string | null
          organization_id: string
          quantity?: number
          updated_at?: string | null
        }
        Update: {
          category?: string | null
          created_at?: string | null
          id?: string
          location_id?: string | null
          name?: string
          notes?: string | null
          organization_id?: string
          quantity?: number
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "inventory_items_location_id_fkey"
            columns: ["location_id"]
            isOneToOne: false
            referencedRelation: "locations"
            referencedColumns: ["id"]
          },
        ]
      }
      locations: {
        Row: {
          created_at: string | null
          id: string
          location_type: string
          name: string
          notes: string | null
          organization_id: string
          parent_id: string | null
          sort_order: number | null
          updated_at: string | null
        }
        Insert: {
          created_at?: string | null
          id?: string
          location_type?: string
          name: string
          notes?: string | null
          organization_id: string
          parent_id?: string | null
          sort_order?: number | null
          updated_at?: string | null
        }
        Update: {
          created_at?: string | null
          id?: string
          location_type?: string
          name?: string
          notes?: string | null
          organization_id?: string
          parent_id?: string | null
          sort_order?: number | null
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "inventory_locations_parent_id_fkey"
            columns: ["parent_id"]
            isOneToOne: false
            referencedRelation: "locations"
            referencedColumns: ["id"]
          },
        ]
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  knowledge: {
    Tables: {
      agent_memory: {
        Row: {
          agent_id: string
          content: string
          created_at: string
          id: string
          organization_id: string
          path: string
          updated_at: string
        }
        Insert: {
          agent_id: string
          content: string
          created_at?: string
          id?: string
          organization_id: string
          path: string
          updated_at?: string
        }
        Update: {
          agent_id?: string
          content?: string
          created_at?: string
          id?: string
          organization_id?: string
          path?: string
          updated_at?: string
        }
        Relationships: []
      }
      audit_log: {
        Row: {
          created_at: string | null
          error_message: string | null
          execution_success: boolean
          feedback_timestamp: string | null
          generated_sql: string
          id: string
          llm_confidence: string | null
          llm_generation_time_ms: number | null
          llm_model: string | null
          organization_id: string | null
          result_count: number | null
          sql_execution_time_ms: number | null
          total_time_ms: number | null
          user_email: string
          user_feedback_comment: string | null
          user_id: string
          user_question: string
          user_rating: number | null
        }
        Insert: {
          created_at?: string | null
          error_message?: string | null
          execution_success?: boolean
          feedback_timestamp?: string | null
          generated_sql: string
          id?: string
          llm_confidence?: string | null
          llm_generation_time_ms?: number | null
          llm_model?: string | null
          organization_id?: string | null
          result_count?: number | null
          sql_execution_time_ms?: number | null
          total_time_ms?: number | null
          user_email: string
          user_feedback_comment?: string | null
          user_id: string
          user_question: string
          user_rating?: number | null
        }
        Update: {
          created_at?: string | null
          error_message?: string | null
          execution_success?: boolean
          feedback_timestamp?: string | null
          generated_sql?: string
          id?: string
          llm_confidence?: string | null
          llm_generation_time_ms?: number | null
          llm_model?: string | null
          organization_id?: string | null
          result_count?: number | null
          sql_execution_time_ms?: number | null
          total_time_ms?: number | null
          user_email?: string
          user_feedback_comment?: string | null
          user_id?: string
          user_question?: string
          user_rating?: number | null
        }
        Relationships: []
      }
      automation_requests: {
        Row: {
          agent_id: string
          conversation_context: Json | null
          created_at: string
          description: string
          id: string
          organization_id: string
          session_id: string | null
          status: string
          title: string
          updated_at: string
          user_id: string | null
        }
        Insert: {
          agent_id: string
          conversation_context?: Json | null
          created_at?: string
          description: string
          id?: string
          organization_id: string
          session_id?: string | null
          status?: string
          title: string
          updated_at?: string
          user_id?: string | null
        }
        Update: {
          agent_id?: string
          conversation_context?: Json | null
          created_at?: string
          description?: string
          id?: string
          organization_id?: string
          session_id?: string | null
          status?: string
          title?: string
          updated_at?: string
          user_id?: string | null
        }
        Relationships: []
      }
      chunks: {
        Row: {
          chunk_index: number
          content: string
          content_tsv: unknown
          created_at: string | null
          document_type: string | null
          document_version: string | null
          drone_model: string | null
          embedding: string | null
          id: string
          manufacturer: string | null
          metadata: Json | null
          org_id: string | null
          source_id: string | null
          source_page_number: number | null
          source_title: string
          source_type: string
          source_url: string | null
          updated_at: string | null
          uploaded_by_user_id: string | null
          visibility: string
        }
        Insert: {
          chunk_index?: number
          content: string
          content_tsv?: unknown
          created_at?: string | null
          document_type?: string | null
          document_version?: string | null
          drone_model?: string | null
          embedding?: string | null
          id?: string
          manufacturer?: string | null
          metadata?: Json | null
          org_id?: string | null
          source_id?: string | null
          source_page_number?: number | null
          source_title: string
          source_type: string
          source_url?: string | null
          updated_at?: string | null
          uploaded_by_user_id?: string | null
          visibility?: string
        }
        Update: {
          chunk_index?: number
          content?: string
          content_tsv?: unknown
          created_at?: string | null
          document_type?: string | null
          document_version?: string | null
          drone_model?: string | null
          embedding?: string | null
          id?: string
          manufacturer?: string | null
          metadata?: Json | null
          org_id?: string | null
          source_id?: string | null
          source_page_number?: number | null
          source_title?: string
          source_type?: string
          source_url?: string | null
          updated_at?: string | null
          uploaded_by_user_id?: string | null
          visibility?: string
        }
        Relationships: []
      }
      rate_limit: {
        Row: {
          id: string
          query_timestamp: string
          user_id: string
        }
        Insert: {
          id?: string
          query_timestamp?: string
          user_id: string
        }
        Update: {
          id?: string
          query_timestamp?: string
          user_id?: string
        }
        Relationships: []
      }
    }
    Views: {
      chunks_catalog: {
        Row: {
          chunk_index: number | null
          created_at: string | null
          document_type: string | null
          document_version: string | null
          drone_model: string | null
          id: string | null
          manufacturer: string | null
          org_id: string | null
          source_id: string | null
          source_page_number: number | null
          source_title: string | null
          source_type: string | null
          source_url: string | null
          updated_at: string | null
          visibility: string | null
        }
        Insert: {
          chunk_index?: number | null
          created_at?: string | null
          document_type?: string | null
          document_version?: string | null
          drone_model?: string | null
          id?: string | null
          manufacturer?: string | null
          org_id?: string | null
          source_id?: string | null
          source_page_number?: number | null
          source_title?: string | null
          source_type?: string | null
          source_url?: string | null
          updated_at?: string | null
          visibility?: string | null
        }
        Update: {
          chunk_index?: number | null
          created_at?: string | null
          document_type?: string | null
          document_version?: string | null
          drone_model?: string | null
          id?: string | null
          manufacturer?: string | null
          org_id?: string | null
          source_id?: string | null
          source_page_number?: number | null
          source_title?: string | null
          source_type?: string | null
          source_url?: string | null
          updated_at?: string | null
          visibility?: string | null
        }
        Relationships: []
      }
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  manufacturing: {
    Tables: {
      bom_lines: {
        Row: {
          bom_id: string
          created_at: string
          id: string
          metadata: Json
          odoo_id: number
          product_id: string
          product_qty: number
          sequence: number
          synced_at: string
          updated_at: string
        }
        Insert: {
          bom_id: string
          created_at?: string
          id?: string
          metadata?: Json
          odoo_id: number
          product_id: string
          product_qty: number
          sequence?: number
          synced_at?: string
          updated_at?: string
        }
        Update: {
          bom_id?: string
          created_at?: string
          id?: string
          metadata?: Json
          odoo_id?: number
          product_id?: string
          product_qty?: number
          sequence?: number
          synced_at?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "bom_lines_bom_id_fkey"
            columns: ["bom_id"]
            isOneToOne: false
            referencedRelation: "boms"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "bom_lines_product_id_fkey"
            columns: ["product_id"]
            isOneToOne: false
            referencedRelation: "products"
            referencedColumns: ["id"]
          },
        ]
      }
      boms: {
        Row: {
          active: boolean
          code: string | null
          created_at: string
          id: string
          metadata: Json
          odoo_id: number
          product_id: string
          product_qty: number
          synced_at: string
          type: string
          updated_at: string
        }
        Insert: {
          active?: boolean
          code?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          odoo_id: number
          product_id: string
          product_qty?: number
          synced_at?: string
          type?: string
          updated_at?: string
        }
        Update: {
          active?: boolean
          code?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          odoo_id?: number
          product_id?: string
          product_qty?: number
          synced_at?: string
          type?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "boms_product_id_fkey"
            columns: ["product_id"]
            isOneToOne: false
            referencedRelation: "products"
            referencedColumns: ["id"]
          },
        ]
      }
      lots: {
        Row: {
          create_date: string | null
          created_at: string
          id: string
          metadata: Json
          name: string
          odoo_id: number
          product_variant_id: string
          ref: string | null
          synced_at: string
          updated_at: string
        }
        Insert: {
          create_date?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          name: string
          odoo_id: number
          product_variant_id: string
          ref?: string | null
          synced_at?: string
          updated_at?: string
        }
        Update: {
          create_date?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          name?: string
          odoo_id?: number
          product_variant_id?: string
          ref?: string | null
          synced_at?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "lots_product_variant_id_fkey"
            columns: ["product_variant_id"]
            isOneToOne: false
            referencedRelation: "product_variants"
            referencedColumns: ["id"]
          },
        ]
      }
      product_variants: {
        Row: {
          active: boolean
          barcode: string | null
          created_at: string
          default_code: string | null
          id: string
          metadata: Json
          name: string
          odoo_id: number
          product_id: string
          synced_at: string
          updated_at: string
        }
        Insert: {
          active?: boolean
          barcode?: string | null
          created_at?: string
          default_code?: string | null
          id?: string
          metadata?: Json
          name: string
          odoo_id: number
          product_id: string
          synced_at?: string
          updated_at?: string
        }
        Update: {
          active?: boolean
          barcode?: string | null
          created_at?: string
          default_code?: string | null
          id?: string
          metadata?: Json
          name?: string
          odoo_id?: number
          product_id?: string
          synced_at?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "product_variants_product_id_fkey"
            columns: ["product_id"]
            isOneToOne: false
            referencedRelation: "products"
            referencedColumns: ["id"]
          },
        ]
      }
      production_orders: {
        Row: {
          bom_id: string | null
          created_at: string
          date_finished: string | null
          date_planned_finished: string | null
          date_planned_start: string | null
          date_start: string | null
          id: string
          metadata: Json
          name: string
          odoo_id: number
          product_id: string
          product_qty: number
          qty_produced: number
          state: string
          synced_at: string
          updated_at: string
        }
        Insert: {
          bom_id?: string | null
          created_at?: string
          date_finished?: string | null
          date_planned_finished?: string | null
          date_planned_start?: string | null
          date_start?: string | null
          id?: string
          metadata?: Json
          name: string
          odoo_id: number
          product_id: string
          product_qty: number
          qty_produced?: number
          state: string
          synced_at?: string
          updated_at?: string
        }
        Update: {
          bom_id?: string | null
          created_at?: string
          date_finished?: string | null
          date_planned_finished?: string | null
          date_planned_start?: string | null
          date_start?: string | null
          id?: string
          metadata?: Json
          name?: string
          odoo_id?: number
          product_id?: string
          product_qty?: number
          qty_produced?: number
          state?: string
          synced_at?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "production_orders_bom_id_fkey"
            columns: ["bom_id"]
            isOneToOne: false
            referencedRelation: "boms"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "production_orders_product_id_fkey"
            columns: ["product_id"]
            isOneToOne: false
            referencedRelation: "products"
            referencedColumns: ["id"]
          },
        ]
      }
      products: {
        Row: {
          active: boolean
          created_at: string
          default_code: string | null
          id: string
          is_manufactured: boolean
          metadata: Json
          name: string
          odoo_id: number
          synced_at: string
          type: string | null
          uom: string | null
          updated_at: string
        }
        Insert: {
          active?: boolean
          created_at?: string
          default_code?: string | null
          id?: string
          is_manufactured?: boolean
          metadata?: Json
          name: string
          odoo_id: number
          synced_at?: string
          type?: string | null
          uom?: string | null
          updated_at?: string
        }
        Update: {
          active?: boolean
          created_at?: string
          default_code?: string | null
          id?: string
          is_manufactured?: boolean
          metadata?: Json
          name?: string
          odoo_id?: number
          synced_at?: string
          type?: string | null
          uom?: string | null
          updated_at?: string
        }
        Relationships: []
      }
      routings: {
        Row: {
          bom_id: string | null
          created_at: string
          id: string
          metadata: Json
          name: string
          odoo_id: number
          sequence: number
          synced_at: string
          time_cycle_manual: number | null
          updated_at: string
          work_center_id: string | null
        }
        Insert: {
          bom_id?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          name: string
          odoo_id: number
          sequence?: number
          synced_at?: string
          time_cycle_manual?: number | null
          updated_at?: string
          work_center_id?: string | null
        }
        Update: {
          bom_id?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          name?: string
          odoo_id?: number
          sequence?: number
          synced_at?: string
          time_cycle_manual?: number | null
          updated_at?: string
          work_center_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "routings_bom_id_fkey"
            columns: ["bom_id"]
            isOneToOne: false
            referencedRelation: "boms"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "routings_work_center_id_fkey"
            columns: ["work_center_id"]
            isOneToOne: false
            referencedRelation: "work_centers"
            referencedColumns: ["id"]
          },
        ]
      }
      stock_quants: {
        Row: {
          available_quantity: number | null
          created_at: string
          id: string
          location_id: number
          location_name: string | null
          metadata: Json
          odoo_id: number
          product_id: string | null
          quantity: number
          reserved_quantity: number
          synced_at: string
          updated_at: string
        }
        Insert: {
          available_quantity?: number | null
          created_at?: string
          id?: string
          location_id: number
          location_name?: string | null
          metadata?: Json
          odoo_id: number
          product_id?: string | null
          quantity?: number
          reserved_quantity?: number
          synced_at?: string
          updated_at?: string
        }
        Update: {
          available_quantity?: number | null
          created_at?: string
          id?: string
          location_id?: number
          location_name?: string | null
          metadata?: Json
          odoo_id?: number
          product_id?: string | null
          quantity?: number
          reserved_quantity?: number
          synced_at?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "stock_quants_product_id_fkey"
            columns: ["product_id"]
            isOneToOne: false
            referencedRelation: "product_variants"
            referencedColumns: ["id"]
          },
        ]
      }
      sync_log: {
        Row: {
          created_at: string
          error: string | null
          finished_at: string | null
          id: string
          metadata: Json
          rows_inserted: number
          rows_skipped: number
          rows_updated: number
          started_at: string
          status: string
          table_name: string
        }
        Insert: {
          created_at?: string
          error?: string | null
          finished_at?: string | null
          id?: string
          metadata?: Json
          rows_inserted?: number
          rows_skipped?: number
          rows_updated?: number
          started_at?: string
          status: string
          table_name: string
        }
        Update: {
          created_at?: string
          error?: string | null
          finished_at?: string | null
          id?: string
          metadata?: Json
          rows_inserted?: number
          rows_skipped?: number
          rows_updated?: number
          started_at?: string
          status?: string
          table_name?: string
        }
        Relationships: []
      }
      work_centers: {
        Row: {
          active: boolean
          capacity: number | null
          code: string | null
          created_at: string
          id: string
          metadata: Json
          name: string
          odoo_id: number
          oee_target: number | null
          synced_at: string
          updated_at: string
        }
        Insert: {
          active?: boolean
          capacity?: number | null
          code?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          name: string
          odoo_id: number
          oee_target?: number | null
          synced_at?: string
          updated_at?: string
        }
        Update: {
          active?: boolean
          capacity?: number | null
          code?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          name?: string
          odoo_id?: number
          oee_target?: number | null
          synced_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      work_orders: {
        Row: {
          created_at: string
          date_finished: string | null
          date_planned_finished: string | null
          date_planned_start: string | null
          date_start: string | null
          duration: number | null
          duration_expected: number | null
          id: string
          metadata: Json
          name: string
          odoo_id: number
          production_order_id: string
          state: string
          synced_at: string
          updated_at: string
          work_center_id: string | null
        }
        Insert: {
          created_at?: string
          date_finished?: string | null
          date_planned_finished?: string | null
          date_planned_start?: string | null
          date_start?: string | null
          duration?: number | null
          duration_expected?: number | null
          id?: string
          metadata?: Json
          name: string
          odoo_id: number
          production_order_id: string
          state: string
          synced_at?: string
          updated_at?: string
          work_center_id?: string | null
        }
        Update: {
          created_at?: string
          date_finished?: string | null
          date_planned_finished?: string | null
          date_planned_start?: string | null
          date_start?: string | null
          duration?: number | null
          duration_expected?: number | null
          id?: string
          metadata?: Json
          name?: string
          odoo_id?: number
          production_order_id?: string
          state?: string
          synced_at?: string
          updated_at?: string
          work_center_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "work_orders_production_order_id_fkey"
            columns: ["production_order_id"]
            isOneToOne: false
            referencedRelation: "production_orders"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "work_orders_work_center_id_fkey"
            columns: ["work_center_id"]
            isOneToOne: false
            referencedRelation: "work_centers"
            referencedColumns: ["id"]
          },
        ]
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  onshape: {
    Tables: {
      assemblies: {
        Row: {
          created_at: string
          document_id: string
          drawing_href: string | null
          element_id: string
          id: string
          metadata: Json
          name: string
          part_number: string | null
          pdf_href: string | null
          revision: string | null
          state: string | null
          synced_at: string
          updated_at: string
          workspace_id: string
        }
        Insert: {
          created_at?: string
          document_id: string
          drawing_href?: string | null
          element_id: string
          id?: string
          metadata?: Json
          name: string
          part_number?: string | null
          pdf_href?: string | null
          revision?: string | null
          state?: string | null
          synced_at?: string
          updated_at?: string
          workspace_id: string
        }
        Update: {
          created_at?: string
          document_id?: string
          drawing_href?: string | null
          element_id?: string
          id?: string
          metadata?: Json
          name?: string
          part_number?: string | null
          pdf_href?: string | null
          revision?: string | null
          state?: string | null
          synced_at?: string
          updated_at?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "assemblies_document_id_fkey"
            columns: ["document_id"]
            isOneToOne: false
            referencedRelation: "documents"
            referencedColumns: ["id"]
          },
        ]
      }
      assembly_lines: {
        Row: {
          assembly_id: string
          child_assembly_id: string | null
          child_part_id: string | null
          created_at: string
          id: string
          metadata: Json
          quantity: number
          sequence: number
          synced_at: string
          updated_at: string
        }
        Insert: {
          assembly_id: string
          child_assembly_id?: string | null
          child_part_id?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          quantity?: number
          sequence?: number
          synced_at?: string
          updated_at?: string
        }
        Update: {
          assembly_id?: string
          child_assembly_id?: string | null
          child_part_id?: string | null
          created_at?: string
          id?: string
          metadata?: Json
          quantity?: number
          sequence?: number
          synced_at?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "assembly_lines_assembly_id_fkey"
            columns: ["assembly_id"]
            isOneToOne: false
            referencedRelation: "assemblies"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "assembly_lines_child_assembly_id_fkey"
            columns: ["child_assembly_id"]
            isOneToOne: false
            referencedRelation: "assemblies"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "assembly_lines_child_part_id_fkey"
            columns: ["child_part_id"]
            isOneToOne: false
            referencedRelation: "parts"
            referencedColumns: ["id"]
          },
        ]
      }
      documents: {
        Row: {
          created_at: string
          created_at_remote: string | null
          default_workspace_id: string | null
          id: string
          metadata: Json
          modified_at_remote: string | null
          name: string
          onshape_id: string
          owner_id: string | null
          owner_type: string | null
          synced_at: string
          thumbnail_url: string | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          created_at_remote?: string | null
          default_workspace_id?: string | null
          id?: string
          metadata?: Json
          modified_at_remote?: string | null
          name: string
          onshape_id: string
          owner_id?: string | null
          owner_type?: string | null
          synced_at?: string
          thumbnail_url?: string | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          created_at_remote?: string | null
          default_workspace_id?: string | null
          id?: string
          metadata?: Json
          modified_at_remote?: string | null
          name?: string
          onshape_id?: string
          owner_id?: string | null
          owner_type?: string | null
          synced_at?: string
          thumbnail_url?: string | null
          updated_at?: string
        }
        Relationships: []
      }
      elements: {
        Row: {
          created_at: string
          document_id: string
          element_id: string
          element_type: string
          id: string
          metadata: Json
          mime_type: string | null
          name: string | null
          part_number: string | null
          synced_at: string
          updated_at: string
          workspace_id: string
        }
        Insert: {
          created_at?: string
          document_id: string
          element_id: string
          element_type: string
          id?: string
          metadata?: Json
          mime_type?: string | null
          name?: string | null
          part_number?: string | null
          synced_at?: string
          updated_at?: string
          workspace_id: string
        }
        Update: {
          created_at?: string
          document_id?: string
          element_id?: string
          element_type?: string
          id?: string
          metadata?: Json
          mime_type?: string | null
          name?: string | null
          part_number?: string | null
          synced_at?: string
          updated_at?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "elements_document_id_fkey"
            columns: ["document_id"]
            isOneToOne: false
            referencedRelation: "documents"
            referencedColumns: ["id"]
          },
        ]
      }
      parts: {
        Row: {
          created_at: string
          document_id: string
          element_id: string
          id: string
          mass_kg: number | null
          material: string | null
          metadata: Json
          name: string
          part_id: string
          part_number: string | null
          revision: string | null
          state: string | null
          synced_at: string
          thumbnail_url: string | null
          updated_at: string
          workspace_id: string
        }
        Insert: {
          created_at?: string
          document_id: string
          element_id: string
          id?: string
          mass_kg?: number | null
          material?: string | null
          metadata?: Json
          name: string
          part_id: string
          part_number?: string | null
          revision?: string | null
          state?: string | null
          synced_at?: string
          thumbnail_url?: string | null
          updated_at?: string
          workspace_id: string
        }
        Update: {
          created_at?: string
          document_id?: string
          element_id?: string
          id?: string
          mass_kg?: number | null
          material?: string | null
          metadata?: Json
          name?: string
          part_id?: string
          part_number?: string | null
          revision?: string | null
          state?: string | null
          synced_at?: string
          thumbnail_url?: string | null
          updated_at?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "parts_document_id_fkey"
            columns: ["document_id"]
            isOneToOne: false
            referencedRelation: "documents"
            referencedColumns: ["id"]
          },
        ]
      }
      sync_log: {
        Row: {
          created_at: string
          error: string | null
          finished_at: string | null
          id: string
          metadata: Json
          rows_inserted: number
          rows_skipped: number
          rows_updated: number
          started_at: string
          status: string
          table_name: string
        }
        Insert: {
          created_at?: string
          error?: string | null
          finished_at?: string | null
          id?: string
          metadata?: Json
          rows_inserted?: number
          rows_skipped?: number
          rows_updated?: number
          started_at?: string
          status: string
          table_name: string
        }
        Update: {
          created_at?: string
          error?: string | null
          finished_at?: string | null
          id?: string
          metadata?: Json
          rows_inserted?: number
          rows_skipped?: number
          rows_updated?: number
          started_at?: string
          status?: string
          table_name?: string
        }
        Relationships: []
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  ops: {
    Tables: {
      customers: {
        Row: {
          created_at: string
          description: string | null
          display_order: number | null
          id: string
          is_active: boolean | null
          name: string
          organization_id: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          display_order?: number | null
          id?: string
          is_active?: boolean | null
          name: string
          organization_id: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          description?: string | null
          display_order?: number | null
          id?: string
          is_active?: boolean | null
          name?: string
          organization_id?: string
          updated_at?: string
        }
        Relationships: []
      }
      equipment: {
        Row: {
          created_at: string | null
          created_by: string | null
          drone_serial: string | null
          id: string
          location: string | null
          name: string
          notes: string | null
          organization_id: string
          photo_url: string | null
          quantity: number
          updated_at: string | null
        }
        Insert: {
          created_at?: string | null
          created_by?: string | null
          drone_serial?: string | null
          id?: string
          location?: string | null
          name: string
          notes?: string | null
          organization_id: string
          photo_url?: string | null
          quantity?: number
          updated_at?: string | null
        }
        Update: {
          created_at?: string | null
          created_by?: string | null
          drone_serial?: string | null
          id?: string
          location?: string | null
          name?: string
          notes?: string | null
          organization_id?: string
          photo_url?: string | null
          quantity?: number
          updated_at?: string | null
        }
        Relationships: []
      }
      flight_equipment: {
        Row: {
          equipment_id: string
          id: string
          planned_flight_id: string
          previous_location: string | null
        }
        Insert: {
          equipment_id: string
          id?: string
          planned_flight_id: string
          previous_location?: string | null
        }
        Update: {
          equipment_id?: string
          id?: string
          planned_flight_id?: string
          previous_location?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "planned_flight_equipment_equipment_id_fkey"
            columns: ["equipment_id"]
            isOneToOne: false
            referencedRelation: "equipment"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "planned_flight_equipment_planned_flight_id_fkey"
            columns: ["planned_flight_id"]
            isOneToOne: false
            referencedRelation: "planned_flights"
            referencedColumns: ["id"]
          },
        ]
      }
      planned_flights: {
        Row: {
          created_at: string | null
          created_by: string
          customer_id: string | null
          description: string | null
          drone_serial: string | null
          id: string
          linked_flight_id: string | null
          organization_id: string
          pilot_id: string | null
          planned_date: string
          planned_end_date: string | null
          project_id: string | null
          status: string
          updated_at: string | null
        }
        Insert: {
          created_at?: string | null
          created_by: string
          customer_id?: string | null
          description?: string | null
          drone_serial?: string | null
          id?: string
          linked_flight_id?: string | null
          organization_id: string
          pilot_id?: string | null
          planned_date: string
          planned_end_date?: string | null
          project_id?: string | null
          status?: string
          updated_at?: string | null
        }
        Update: {
          created_at?: string | null
          created_by?: string
          customer_id?: string | null
          description?: string | null
          drone_serial?: string | null
          id?: string
          linked_flight_id?: string | null
          organization_id?: string
          pilot_id?: string | null
          planned_date?: string
          planned_end_date?: string | null
          project_id?: string | null
          status?: string
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "planned_flights_customer_id_fkey"
            columns: ["customer_id"]
            isOneToOne: false
            referencedRelation: "customers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "planned_flights_project_id_fkey"
            columns: ["project_id"]
            isOneToOne: false
            referencedRelation: "projects"
            referencedColumns: ["id"]
          },
        ]
      }
      projects: {
        Row: {
          created_at: string
          description: string | null
          display_order: number | null
          id: string
          is_active: boolean | null
          name: string
          organization_id: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          display_order?: number | null
          id?: string
          is_active?: boolean | null
          name: string
          organization_id: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          description?: string | null
          display_order?: number | null
          id?: string
          is_active?: boolean | null
          name?: string
          organization_id?: string
          updated_at?: string
        }
        Relationships: []
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  public: {
    Tables: {
      flash_events: {
        Row: {
          build_dirty: boolean | null
          build_environment: Json | null
          calibration_commit: string | null
          calibration_committed_at: string | null
          flash_type: string
          flashed_at: string | null
          git_branch: string | null
          git_commit: string
          hex_sha256: string | null
          id: string
          is_test_board: boolean
          notes: string | null
          operator_id: string | null
          release_id: string | null
          serial_number: string
        }
        Insert: {
          build_dirty?: boolean | null
          build_environment?: Json | null
          calibration_commit?: string | null
          calibration_committed_at?: string | null
          flash_type: string
          flashed_at?: string | null
          git_branch?: string | null
          git_commit: string
          hex_sha256?: string | null
          id?: string
          is_test_board?: boolean
          notes?: string | null
          operator_id?: string | null
          release_id?: string | null
          serial_number: string
        }
        Update: {
          build_dirty?: boolean | null
          build_environment?: Json | null
          calibration_commit?: string | null
          calibration_committed_at?: string | null
          flash_type?: string
          flashed_at?: string | null
          git_branch?: string | null
          git_commit?: string
          hex_sha256?: string | null
          id?: string
          is_test_board?: boolean
          notes?: string | null
          operator_id?: string | null
          release_id?: string | null
          serial_number?: string
        }
        Relationships: [
          {
            foreignKeyName: "flash_events_release_id_fkey"
            columns: ["release_id"]
            isOneToOne: false
            referencedRelation: "releases"
            referencedColumns: ["id"]
          },
        ]
      }
      notes: {
        Row: {
          attachments: Json | null
          author_id: string | null
          content: string
          created_at: string | null
          date_of_action: string | null
          drone_serial: string | null
          id: string
          note_type: string
          organization_id: string | null
          priority: string | null
          resolution_notes: string | null
          resolved_at: string | null
          resolved_by: string | null
          subject: string
          updated_at: string | null
        }
        Insert: {
          attachments?: Json | null
          author_id?: string | null
          content: string
          created_at?: string | null
          date_of_action?: string | null
          drone_serial?: string | null
          id?: string
          note_type: string
          organization_id?: string | null
          priority?: string | null
          resolution_notes?: string | null
          resolved_at?: string | null
          resolved_by?: string | null
          subject: string
          updated_at?: string | null
        }
        Update: {
          attachments?: Json | null
          author_id?: string | null
          content?: string
          created_at?: string | null
          date_of_action?: string | null
          drone_serial?: string | null
          id?: string
          note_type?: string
          organization_id?: string | null
          priority?: string | null
          resolution_notes?: string | null
          resolved_at?: string | null
          resolved_by?: string | null
          subject?: string
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "notes_author_id_fkey"
            columns: ["author_id"]
            isOneToOne: false
            referencedRelation: "profile_names"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "notes_author_id_fkey"
            columns: ["author_id"]
            isOneToOne: false
            referencedRelation: "profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "notes_drone_serial_fkey"
            columns: ["drone_serial"]
            isOneToOne: false
            referencedRelation: "drone_firmware_status"
            referencedColumns: ["serial_number"]
          },
          {
            foreignKeyName: "notes_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "notes_resolved_by_fkey"
            columns: ["resolved_by"]
            isOneToOne: false
            referencedRelation: "profile_names"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "notes_resolved_by_fkey"
            columns: ["resolved_by"]
            isOneToOne: false
            referencedRelation: "profiles"
            referencedColumns: ["id"]
          },
        ]
      }
      onboard_unit_events: {
        Row: {
          created_at: string
          drone_serial: string | null
          id: string
          kind: string
          occurred_at: string
          operator_id: string | null
          payload: Json | null
          tag: string
        }
        Insert: {
          created_at?: string
          drone_serial?: string | null
          id?: string
          kind: string
          occurred_at?: string
          operator_id?: string | null
          payload?: Json | null
          tag: string
        }
        Update: {
          created_at?: string
          drone_serial?: string | null
          id?: string
          kind?: string
          occurred_at?: string
          operator_id?: string | null
          payload?: Json | null
          tag?: string
        }
        Relationships: [
          {
            foreignKeyName: "onboard_unit_events_drone_serial_fkey"
            columns: ["drone_serial"]
            isOneToOne: false
            referencedRelation: "drone_firmware_status"
            referencedColumns: ["serial_number"]
          },
          {
            foreignKeyName: "onboard_unit_events_tag_fkey"
            columns: ["tag"]
            isOneToOne: false
            referencedRelation: "onboard_units"
            referencedColumns: ["tag"]
          },
        ]
      }
      onboard_units: {
        Row: {
          carrier: string | null
          carrier_serial: string | null
          created_at: string
          hostname: string | null
          installed_in: string | null
          last_manifest: Json | null
          last_seen: string | null
          location: string | null
          mac: string | null
          module_part: string | null
          module_serial: string
          notes: string | null
          organization_id: string
          role: string
          tag: string
          updated_at: string
        }
        Insert: {
          carrier?: string | null
          carrier_serial?: string | null
          created_at?: string
          hostname?: string | null
          installed_in?: string | null
          last_manifest?: Json | null
          last_seen?: string | null
          location?: string | null
          mac?: string | null
          module_part?: string | null
          module_serial: string
          notes?: string | null
          organization_id: string
          role: string
          tag: string
          updated_at?: string
        }
        Update: {
          carrier?: string | null
          carrier_serial?: string | null
          created_at?: string
          hostname?: string | null
          installed_in?: string | null
          last_manifest?: Json | null
          last_seen?: string | null
          location?: string | null
          mac?: string | null
          module_part?: string | null
          module_serial?: string
          notes?: string | null
          organization_id?: string
          role?: string
          tag?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "onboard_units_installed_in_fkey"
            columns: ["installed_in"]
            isOneToOne: false
            referencedRelation: "drone_firmware_status"
            referencedColumns: ["serial_number"]
          },
          {
            foreignKeyName: "onboard_units_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
        ]
      }
      organization_documents: {
        Row: {
          content_type: string | null
          created_at: string
          description: string | null
          document_type: Database["public"]["Enums"]["document_type_enum"]
          file_size: number | null
          id: string
          organization_id: string
          original_filename: string
          s3_key: string
          title: string
          updated_at: string
          uploaded_by: string | null
          validity_end_date: string | null
          validity_start_date: string | null
        }
        Insert: {
          content_type?: string | null
          created_at?: string
          description?: string | null
          document_type?: Database["public"]["Enums"]["document_type_enum"]
          file_size?: number | null
          id?: string
          organization_id: string
          original_filename: string
          s3_key: string
          title: string
          updated_at?: string
          uploaded_by?: string | null
          validity_end_date?: string | null
          validity_start_date?: string | null
        }
        Update: {
          content_type?: string | null
          created_at?: string
          description?: string | null
          document_type?: Database["public"]["Enums"]["document_type_enum"]
          file_size?: number | null
          id?: string
          organization_id?: string
          original_filename?: string
          s3_key?: string
          title?: string
          updated_at?: string
          uploaded_by?: string | null
          validity_end_date?: string | null
          validity_start_date?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "organization_documents_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "organization_documents_uploaded_by_fkey"
            columns: ["uploaded_by"]
            isOneToOne: false
            referencedRelation: "profile_names"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "organization_documents_uploaded_by_fkey"
            columns: ["uploaded_by"]
            isOneToOne: false
            referencedRelation: "profiles"
            referencedColumns: ["id"]
          },
        ]
      }
      organizations: {
        Row: {
          created_at: string | null
          id: string
          name: string
          slug: string
        }
        Insert: {
          created_at?: string | null
          id?: string
          name: string
          slug: string
        }
        Update: {
          created_at?: string | null
          id?: string
          name?: string
          slug?: string
        }
        Relationships: []
      }
      profiles: {
        Row: {
          bio: string | null
          certifications: Json | null
          created_at: string | null
          email: string
          full_name: string | null
          id: string
          legacy_flight_hours: Json | null
          organization_id: string | null
          profile_picture_s3_key: string | null
          updated_at: string | null
        }
        Insert: {
          bio?: string | null
          certifications?: Json | null
          created_at?: string | null
          email: string
          full_name?: string | null
          id: string
          legacy_flight_hours?: Json | null
          organization_id?: string | null
          profile_picture_s3_key?: string | null
          updated_at?: string | null
        }
        Update: {
          bio?: string | null
          certifications?: Json | null
          created_at?: string | null
          email?: string
          full_name?: string | null
          id?: string
          legacy_flight_hours?: Json | null
          organization_id?: string | null
          profile_picture_s3_key?: string | null
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "profiles_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
        ]
      }
      release_binaries: {
        Row: {
          built_at: string | null
          calibration_commit: string | null
          calibration_committed_at: string | null
          hex_sha256: string
          hex_size_bytes: number | null
          id: string
          release_id: string
          serial_number: string
          storage_path: string
        }
        Insert: {
          built_at?: string | null
          calibration_commit?: string | null
          calibration_committed_at?: string | null
          hex_sha256: string
          hex_size_bytes?: number | null
          id?: string
          release_id: string
          serial_number: string
          storage_path: string
        }
        Update: {
          built_at?: string | null
          calibration_commit?: string | null
          calibration_committed_at?: string | null
          hex_sha256?: string
          hex_size_bytes?: number | null
          id?: string
          release_id?: string
          serial_number?: string
          storage_path?: string
        }
        Relationships: [
          {
            foreignKeyName: "release_binaries_release_id_fkey"
            columns: ["release_id"]
            isOneToOne: false
            referencedRelation: "releases"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "release_binaries_serial_number_fkey"
            columns: ["serial_number"]
            isOneToOne: false
            referencedRelation: "drone_firmware_status"
            referencedColumns: ["serial_number"]
          },
        ]
      }
      releases: {
        Row: {
          base_tag: string | null
          calibration_source_commit: string | null
          git_commit: string
          id: string
          kind: string
          release_notes: string | null
          released_at: string | null
          released_by: string | null
          tag: string
        }
        Insert: {
          base_tag?: string | null
          calibration_source_commit?: string | null
          git_commit: string
          id?: string
          kind?: string
          release_notes?: string | null
          released_at?: string | null
          released_by?: string | null
          tag: string
        }
        Update: {
          base_tag?: string | null
          calibration_source_commit?: string | null
          git_commit?: string
          id?: string
          kind?: string
          release_notes?: string | null
          released_at?: string | null
          released_by?: string | null
          tag?: string
        }
        Relationships: []
      }
      reports: {
        Row: {
          created_at: string
          file_size: number | null
          filters: Json
          generated_at: string
          generated_by: string | null
          id: string
          organization_id: string
          report_type: string
          s3_key: string
          summary: Json | null
          title: string
        }
        Insert: {
          created_at?: string
          file_size?: number | null
          filters?: Json
          generated_at?: string
          generated_by?: string | null
          id?: string
          organization_id: string
          report_type?: string
          s3_key: string
          summary?: Json | null
          title: string
        }
        Update: {
          created_at?: string
          file_size?: number | null
          filters?: Json
          generated_at?: string
          generated_by?: string | null
          id?: string
          organization_id?: string
          report_type?: string
          s3_key?: string
          summary?: Json | null
          title?: string
        }
        Relationships: [
          {
            foreignKeyName: "reports_generated_by_fkey"
            columns: ["generated_by"]
            isOneToOne: false
            referencedRelation: "profile_names"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "reports_generated_by_fkey"
            columns: ["generated_by"]
            isOneToOne: false
            referencedRelation: "profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "reports_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
        ]
      }
      software_download_events: {
        Row: {
          downloaded_at: string
          id: string
          organization_id: string
          product: string
          release_id: string | null
          user_id: string | null
          version: string
        }
        Insert: {
          downloaded_at?: string
          id?: string
          organization_id: string
          product: string
          release_id?: string | null
          user_id?: string | null
          version: string
        }
        Update: {
          downloaded_at?: string
          id?: string
          organization_id?: string
          product?: string
          release_id?: string | null
          user_id?: string | null
          version?: string
        }
        Relationships: [
          {
            foreignKeyName: "software_download_events_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "software_download_events_release_id_fkey"
            columns: ["release_id"]
            isOneToOne: false
            referencedRelation: "software_releases"
            referencedColumns: ["id"]
          },
        ]
      }
      software_products: {
        Row: {
          archived: boolean
          audience: string
          created_at: string
          created_by: string | null
          key: string
          label: string
          sort_order: number
        }
        Insert: {
          archived?: boolean
          audience: string
          created_at?: string
          created_by?: string | null
          key: string
          label: string
          sort_order: number
        }
        Update: {
          archived?: boolean
          audience?: string
          created_at?: string
          created_by?: string | null
          key?: string
          label?: string
          sort_order?: number
        }
        Relationships: []
      }
      software_releases: {
        Row: {
          audience: string
          channel: string
          file_name: string
          id: string
          product: string
          release_notes: string | null
          released_at: string
          released_by: string | null
          s3_key: string
          sha256: string
          size_bytes: number
          version: string
        }
        Insert: {
          audience: string
          channel?: string
          file_name: string
          id?: string
          product: string
          release_notes?: string | null
          released_at?: string
          released_by?: string | null
          s3_key: string
          sha256: string
          size_bytes: number
          version: string
        }
        Update: {
          audience?: string
          channel?: string
          file_name?: string
          id?: string
          product?: string
          release_notes?: string | null
          released_at?: string
          released_by?: string | null
          s3_key?: string
          sha256?: string
          size_bytes?: number
          version?: string
        }
        Relationships: [
          {
            foreignKeyName: "software_releases_product_fkey"
            columns: ["product"]
            isOneToOne: false
            referencedRelation: "software_products"
            referencedColumns: ["key"]
          },
        ]
      }
      user_invitations: {
        Row: {
          accepted_at: string | null
          created_at: string
          email: string
          expires_at: string
          id: string
          invited_by: string
          organization_id: string
          roles: string[]
          token: string
          updated_at: string
        }
        Insert: {
          accepted_at?: string | null
          created_at?: string
          email: string
          expires_at: string
          id?: string
          invited_by: string
          organization_id: string
          roles: string[]
          token: string
          updated_at?: string
        }
        Update: {
          accepted_at?: string | null
          created_at?: string
          email?: string
          expires_at?: string
          id?: string
          invited_by?: string
          organization_id?: string
          roles?: string[]
          token?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "user_invitations_invited_by_fkey"
            columns: ["invited_by"]
            isOneToOne: false
            referencedRelation: "profile_names"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "user_invitations_invited_by_fkey"
            columns: ["invited_by"]
            isOneToOne: false
            referencedRelation: "profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "user_invitations_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
        ]
      }
      user_roles: {
        Row: {
          created_at: string | null
          created_by: string | null
          id: string
          role: Database["public"]["Enums"]["user_role"]
          user_id: string
        }
        Insert: {
          created_at?: string | null
          created_by?: string | null
          id?: string
          role: Database["public"]["Enums"]["user_role"]
          user_id: string
        }
        Update: {
          created_at?: string | null
          created_by?: string | null
          id?: string
          role?: Database["public"]["Enums"]["user_role"]
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "user_roles_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "profile_names"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "user_roles_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "user_roles_user_id_fkey"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "profile_names"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "user_roles_user_id_fkey"
            columns: ["user_id"]
            isOneToOne: false
            referencedRelation: "profiles"
            referencedColumns: ["id"]
          },
        ]
      }
    }
    Views: {
      drone_firmware_status: {
        Row: {
          current_firmware: string | null
          last_flash_commit: string | null
          last_flash_date: string | null
          last_flash_operator: string | null
          last_flash_type: string | null
          model: string | null
          organization_id: string | null
          serial_number: string | null
        }
        Relationships: [
          {
            foreignKeyName: "drones_organization_id_fkey"
            columns: ["organization_id"]
            isOneToOne: false
            referencedRelation: "organizations"
            referencedColumns: ["id"]
          },
        ]
      }
      profile_names: {
        Row: {
          full_name: string | null
          id: string | null
        }
        Insert: {
          full_name?: string | null
          id?: string | null
        }
        Update: {
          full_name?: string | null
          id?: string | null
        }
        Relationships: []
      }
    }
    Functions: {
      calculate_drone_engine_hours: {
        Args: { drone_serial_param: string }
        Returns: number
      }
      calculate_drone_flight_hours: {
        Args: { drone_serial_param: string }
        Returns: number
      }
      check_llm_rate_limit: {
        Args: {
          p_max_queries?: number
          p_user_id: string
          p_window_hours?: number
        }
        Returns: {
          allowed: boolean
          current_count: number
          limit_count: number
          reset_time: string
        }[]
      }
      cleanup_old_rate_limits: { Args: never; Returns: number }
      clear_drone_uc_fw: {
        Args: { p_serial_number: string }
        Returns: undefined
      }
      current_org_id: { Args: never; Returns: string }
      custom_access_token_hook: { Args: { event: Json }; Returns: Json }
      drone_org: { Args: { p_serial: string }; Returns: string }
      execute_read_query: { Args: { query_sql: string }; Returns: Json }
      get_llm_query_stats: {
        Args: {
          p_days?: number
          p_organization_id?: string
          p_user_id?: string
        }
        Returns: {
          avg_llm_time_ms: number
          avg_sql_time_ms: number
          avg_total_time_ms: number
          failed_queries: number
          negative_feedback: number
          positive_feedback: number
          queries_with_feedback: number
          successful_queries: number
          total_queries: number
        }[]
      }
      get_notes_date_range: {
        Args: { p_drone_serial: string }
        Returns: {
          earliest_date: string
          latest_date: string
        }[]
      }
      is_org_admin: { Args: never; Returns: boolean }
      is_xer_admin: { Args: never; Returns: boolean }
      is_xer_employee: { Args: never; Returns: boolean }
      match_knowledge_chunks: {
        Args: {
          filter_source_type?: string
          match_count?: number
          match_threshold?: number
          query_embedding: string
        }
        Returns: {
          content: string
          document_type: string
          document_version: string
          drone_model: string
          id: string
          metadata: Json
          similarity: number
          source_id: string
          source_page_number: number
          source_title: string
          source_type: string
          source_url: string
        }[]
      }
      match_knowledge_hybrid: {
        Args: {
          filter_drone_serial?: string
          filter_site?: string
          filter_source_types?: string[]
          match_count?: number
          match_threshold?: number
          query_embedding: string
          query_text: string
          rrf_k?: number
        }
        Returns: {
          content: string
          document_type: string
          document_version: string
          drone_model: string
          id: string
          metadata: Json
          rrf_score: number
          similarity: number
          source_id: string
          source_page_number: number
          source_title: string
          source_type: string
          source_url: string
          text_rank: number
        }[]
      }
      recalculate_drone_maintenance: {
        Args: { target_drone_serial: string }
        Returns: undefined
      }
      update_drone_uc_calibration: {
        Args: {
          p_calibration_commit: string
          p_calibration_committed_at: string
          p_serial_number: string
        }
        Returns: undefined
      }
      update_drone_uc_fw: {
        Args: { p_serial_number: string; p_uc_fw: string }
        Returns: undefined
      }
    }
    Enums: {
      document_type_enum: "insurance" | "permit" | "other"
      drone_ownership_type: "SOLD" | "LEASED"
      drone_status:
        | "Airworthy"
        | "Airworthy with limitations"
        | "Maintenance"
        | "Not Airworthy"
        | "With customer"
        | "In production"
      user_role:
        | "admin"
        | "leader"
        | "pilot"
        | "maintenance"
        | "airspace_observer"
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  website_analytics: {
    Tables: {
      action_log: {
        Row: {
          action_date: string
          created_at: string | null
          created_by: string
          description: string
          entry_type: string
          id: string
          updated_at: string | null
        }
        Insert: {
          action_date: string
          created_at?: string | null
          created_by: string
          description: string
          entry_type?: string
          id?: string
          updated_at?: string | null
        }
        Update: {
          action_date?: string
          created_at?: string | null
          created_by?: string
          description?: string
          entry_type?: string
          id?: string
          updated_at?: string | null
        }
        Relationships: []
      }
      digests: {
        Row: {
          created_at: string | null
          html_content: string | null
          id: string
          sent_at: string | null
          subject: string
          summary: string | null
          week_start: string
        }
        Insert: {
          created_at?: string | null
          html_content?: string | null
          id?: string
          sent_at?: string | null
          subject: string
          summary?: string | null
          week_start: string
        }
        Update: {
          created_at?: string | null
          html_content?: string | null
          id?: string
          sent_at?: string | null
          subject?: string
          summary?: string | null
          week_start?: string
        }
        Relationships: []
      }
      goals: {
        Row: {
          created_at: string
          created_by: string
          description: string | null
          goal_type: string
          id: string
          metric_name: string | null
          name: string
          status: string
          target_date: string | null
          target_value: number | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          created_by: string
          description?: string | null
          goal_type: string
          id?: string
          metric_name?: string | null
          name: string
          status?: string
          target_date?: string | null
          target_value?: number | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          created_by?: string
          description?: string | null
          goal_type?: string
          id?: string
          metric_name?: string | null
          name?: string
          status?: string
          target_date?: string | null
          target_value?: number | null
          updated_at?: string
        }
        Relationships: []
      }
      insight_feedback: {
        Row: {
          created_at: string
          created_by: string
          feedback_type: string
          id: string
          insight_key: string
          rationale: string | null
          week_start: string
        }
        Insert: {
          created_at?: string
          created_by: string
          feedback_type: string
          id?: string
          insight_key: string
          rationale?: string | null
          week_start: string
        }
        Update: {
          created_at?: string
          created_by?: string
          feedback_type?: string
          id?: string
          insight_key?: string
          rationale?: string | null
          week_start?: string
        }
        Relationships: []
      }
      insights: {
        Row: {
          ga4_summary: Json | null
          generated_at: string | null
          goal_progress: Json
          id: string
          issues: Json
          recommendations: Json
          week_start: string
        }
        Insert: {
          ga4_summary?: Json | null
          generated_at?: string | null
          goal_progress?: Json
          id?: string
          issues?: Json
          recommendations?: Json
          week_start: string
        }
        Update: {
          ga4_summary?: Json | null
          generated_at?: string | null
          goal_progress?: Json
          id?: string
          issues?: Json
          recommendations?: Json
          week_start?: string
        }
        Relationships: []
      }
      strategy: {
        Row: {
          content: string
          id: string
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          content?: string
          id?: string
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          content?: string
          id?: string
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: []
      }
      strategy_history: {
        Row: {
          content: string
          id: string
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          content: string
          id?: string
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          content?: string
          id?: string
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: []
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends (DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never) = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] &
        DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] &
        DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends (DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never) = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I
      }
      ? I
      : never
    : never

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends (DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never) = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U
      }
      ? U
      : never
    : never

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends (DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never) = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends (PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never) = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never

export const Constants = {
  comms: {
    Enums: {},
  },
  fleet: {
    Enums: {},
  },
  inventory: {
    Enums: {},
  },
  knowledge: {
    Enums: {},
  },
  manufacturing: {
    Enums: {},
  },
  onshape: {
    Enums: {},
  },
  ops: {
    Enums: {},
  },
  public: {
    Enums: {
      document_type_enum: ["insurance", "permit", "other"],
      drone_ownership_type: ["SOLD", "LEASED"],
      drone_status: [
        "Airworthy",
        "Airworthy with limitations",
        "Maintenance",
        "Not Airworthy",
        "With customer",
        "In production",
      ],
      user_role: [
        "admin",
        "leader",
        "pilot",
        "maintenance",
        "airspace_observer",
      ],
    },
  },
  website_analytics: {
    Enums: {},
  },
} as const
